import {
  ClickPoint,
  IMAGE_PLACE,
  TPollsListResponse,
  TSavedPollResult,
  TSinglePollDetails,
  TSinglePollResult,
} from '../types/pollsSchema';

const IMAGE_SELECTION_TYPES = new Set([
  'couple',
  'range',
  'best_from_many',
  'multiple',
  'tourney',
]);

interface IPollAnswerSource {
  id: number;
  type?: string;
  moderation_status?: TSinglePollDetails['moderation_status'];
  moderation_comment?: string | null;
  answer_media?: TSavedPollResult['answer_media'];
  result?: TSavedPollResult | null;
  reworkBaseline?: TSinglePollDetails['reworkBaseline'];
}

const IMAGE_PLACE_INDEX = [0, 1, 2, 3, 4, 5, 6, 7] as const;

export const isPartRejected = (part?: IPollAnswerSource | null): boolean =>
  part?.moderation_status === 'rejected' ||
  part?.result?.moderation_status === 'rejected';

export const getRejectedComment = (part?: IPollAnswerSource | null): string => {
  if (!isPartRejected(part)) return '';
  return part?.moderation_comment || part?.result?.moderation_comment || '';
};

export const hasSubmittedAnswer = (
  part?: IPollAnswerSource | null,
): boolean => {
  const result = part?.result;
  if (!result) return false;

  return Boolean(
    result.message?.trim() ||
      result.answer_media?.link ||
      result.images?.length ||
      result.rate ||
      result.click_coordinates?.length,
  );
};

export const partNeedsAnswer = (part?: IPollAnswerSource | null): boolean =>
  !hasSubmittedAnswer(part) || isPartRejected(part);

const getPartModerationStatus = (part?: IPollAnswerSource | null) =>
  part?.moderation_status ?? part?.result?.moderation_status ?? null;

/** Accepted and pending parts must not be sent again — API rejects them. */
export const shouldSubmitPart = (part?: IPollAnswerSource | null): boolean => {
  const status = getPartModerationStatus(part);
  if (status === 'approved' || status === 'pending') return false;
  return partNeedsAnswer(part);
};

const mergeSavedResult = (
  primary?: TSavedPollResult | null,
  fallback?: TSavedPollResult | null,
): TSavedPollResult | null => {
  if (!primary && !fallback) return null;

  return {
    message: primary?.message ?? fallback?.message ?? null,
    answer_media: primary?.answer_media?.link
      ? primary.answer_media
      : fallback?.answer_media ?? primary?.answer_media ?? null,
    images: primary?.images?.length ? primary.images : fallback?.images ?? null,
    rate: primary?.rate || fallback?.rate || null,
    click_coordinates: primary?.click_coordinates?.length
      ? primary.click_coordinates
      : fallback?.click_coordinates ?? null,
    moderation_status:
      primary?.moderation_status ?? fallback?.moderation_status ?? null,
    moderation_comment:
      primary?.moderation_comment ?? fallback?.moderation_comment ?? null,
  };
};

export const enrichPollPart = (
  part: TSinglePollDetails,
  list?: TPollsListResponse[],
): TSinglePollDetails => {
  const fromList = list?.find(item => item.id === part.id);
  const mergedResult = mergeSavedResult(part.result, fromList?.result);
  const result = hasSubmittedAnswer({ id: part.id, result: mergedResult })
    ? mergedResult
    : part.result ?? null;

  return {
    ...part,
    moderation_status:
      part.moderation_status ??
      fromList?.moderation_status ??
      fromList?.result?.moderation_status ??
      mergedResult?.moderation_status ??
      null,
    moderation_comment:
      part.moderation_comment ??
      fromList?.moderation_comment ??
      fromList?.result?.moderation_comment ??
      mergedResult?.moderation_comment ??
      null,
    result,
  };
};

const readAnswerMedia = (
  part: IPollAnswerSource,
): TSinglePollResult['answer_media'] => {
  const media = part.answer_media?.link
    ? part.answer_media
    : part.result?.answer_media;
  if (!media?.link || media.link.startsWith('blob:')) return undefined;
  return media;
};

const answerMediaKey = (
  media?: TSinglePollResult['answer_media'] | null,
): string => {
  const link = (media?.link || '').split('?')[0];
  if (link) return link;
  if (media?.id != null) return `id:${media.id}`;
  return '';
};

export const withReworkBaseline = (
  parts: TSinglePollDetails[],
  results: TSinglePollResult[],
): TSinglePollDetails[] =>
  parts.map(part => {
    if (part.type !== 'task' || !isPartRejected(part)) return part;

    const current = results.find(
      item => String(item.poll_id) === String(part.id),
    );

    return {
      ...part,
      reworkBaseline: {
        message: (current?.message || '').trim(),
        mediaKey: answerMediaKey(current?.answer_media),
      },
    };
  });

export const isRejectedTaskAnswerUnchanged = (
  part: IPollAnswerSource | null | undefined,
  current?: Pick<TSinglePollResult, 'message' | 'answer_media'> | null,
): boolean => {
  if (part?.type !== 'task' || !isPartRejected(part) || !part.reworkBaseline) {
    return false;
  }

  const sameMessage =
    (current?.message || '').trim() === part.reworkBaseline.message;
  const sameMedia =
    answerMediaKey(current?.answer_media) === part.reworkBaseline.mediaKey;

  return sameMessage && sameMedia;
};

export const buildPollResultFromPart = (
  part: IPollAnswerSource,
): TSinglePollResult => {
  const imageFields: Partial<TSinglePollResult> = {};
  if (part.type && IMAGE_SELECTION_TYPES.has(part.type)) {
    part.result?.images?.forEach((image, index) => {
      const placeIndex = IMAGE_PLACE_INDEX[index];
      if (placeIndex === undefined || image?.id == null) return;
      imageFields[IMAGE_PLACE[placeIndex]] = image.id;
    });
  }

  return {
    poll_id: part.id,
    message: part.result?.message || '',
    answer_media: readAnswerMedia(part),
    ...(part.result?.rate ? { rate: part.result.rate } : {}),
    ...(part.result?.click_coordinates?.length
      ? { click_coordinates: part.result.click_coordinates }
      : {}),
    ...imageFields,
  };
};

export const pointsFromPart = (part: IPollAnswerSource): ClickPoint[] => {
  const coordinates = part.result?.click_coordinates;
  if (!coordinates?.length) return [];

  return coordinates.map((point, index) => ({
    x: point.x,
    y: point.y,
    id: point.id || `${part.id}-${index}`,
  }));
};

export const isPollRework = (parts: IPollAnswerSource[]): boolean =>
  parts.some(part => isPartRejected(part) && hasSubmittedAnswer(part));

const getFirstAnswerStage = (parts: IPollAnswerSource[]): number => {
  const stage = parts.findIndex(part => partNeedsAnswer(part));
  return stage === -1 ? 0 : stage;
};

export const getAnswerStage = (
  parts: IPollAnswerSource[],
  partId?: number,
): number => {
  if (partId) {
    const stage = parts.findIndex(part => part.id === partId);
    if (stage >= 0) return stage;
  }

  return getFirstAnswerStage(parts);
};

export const getPollEntryTarget = (data: {
  id: number;
  is_composite?: boolean;
  composite_main_id?: number | null;
}): { testId: number; search: string } => {
  const testId =
    data.is_composite && data.composite_main_id
      ? data.composite_main_id
      : data.id;

  return {
    testId,
    search: data.is_composite ? `?part=${data.id}` : '',
  };
};
